import { legacyKey, type OrgTreeRow, type OrgUnitRow } from "@/lib/diagnostic-org-tree";

/**
 * "Copiar resumo da validação" (EF-DIAG-002, RF-07): a validação em texto
 * tabulado, para colar em ata ou planilha.
 *
 * Pura: os textos chegam traduzidos em `labels`, para o resumo sair no idioma da
 * tela sem a função depender do i18n.
 */
export interface SummaryLabels {
  /** As 6 colunas: objeto, chave legado, chave Public, confere, escopo, observação. */
  header: string[];
  yes: string;
  no: string;
  decisions: Record<"pending" | "in_scope" | "client_validation" | "out_of_scope", string>;
  /** Nome do tipo do objeto ("Centro"), pelo código do nível. */
  typeLabel: (levelCode: string, levelName: string) => string;
}

/**
 * O resumo do que está na tela: a árvore com o filtro ativo, inteira, mesmo com
 * linhas recolhidas, na ordem em que aparece. A unidade N:M sai uma vez, e as
 * linhas "Nenhum(a) … no legado" não entram, porque não são objeto.
 *
 * A chave Public sai vazia até existir a chave proposta (F7). O escopo é o que
 * vale para o objeto: o depósito de um centro fora sai como "fora do escopo".
 */
export function validationSummaryTsv(rows: OrgTreeRow[], labels: SummaryLabels): string {
  const seen = new Set<string>();
  const lines = [labels.header.map(cell)];

  const walk = (list: OrgTreeRow[]) => {
    for (const row of list) {
      if (row.kind === "empty") continue;
      if (row.kind === "unit") {
        const id = `${row.node.item_type}:${row.node.item_key}`;
        if (!seen.has(id)) {
          seen.add(id);
          lines.push(summaryLine(row, labels).map(cell));
        }
      }
      walk(row.children);
    }
  };
  walk(rows);

  return lines.map((line) => line.join("\t")).join("\n");
}

function summaryLine({ node, level }: OrgUnitRow, labels: SummaryLabels): string[] {
  const matches = node.validation.data_matches;
  return [
    `${labels.typeLabel(level.level_code, level.level_name)} · ${node.name ?? node.id}`,
    keyCell(legacyKey(node)),
    keyCell(node.proposed_key ?? ""),
    matches === true ? labels.yes : matches === false ? labels.no : "",
    labels.decisions[node.effective_decision ?? "pending"],
    node.validation.note ?? "",
  ];
}

/**
 * O apóstrofo na frente é o jeito de a planilha (Excel, Google Sheets) ler a
 * célula como texto, e ele não aparece no valor. Vale para dois casos:
 *
 * - texto que começa como fórmula (`=`, `+`, `-`, `@`): sem ele, "-10% de
 *   volume" vira erro, e uma observação maliciosa vira fórmula na planilha do
 *   cliente;
 * - chave SAP que a planilha leria como número: "001" perde os zeros, e "1E01"
 *   vira 10. Chave assim deixa de bater com o SAP.
 */
const STARTS_LIKE_FORMULA = /^[=+\-@]/;
const READS_AS_NUMBER = /^(0\d+|\d+(\.\d+)?[eE][+-]?\d+)$/;

function keyCell(key: string): string {
  return READS_AS_NUMBER.test(key) ? `'${key}` : key;
}

/** Tab e quebra de linha viram espaço: dentro da célula, eles quebrariam as colunas. */
function cell(value: string): string {
  const flat = value.replace(/[\t\r\n]+/g, " ").trim();
  return STARTS_LIKE_FORMULA.test(flat) ? `'${flat}` : flat;
}
